import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, ClipboardList, CheckCircle2, Clock, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { getOrders, formatCents } from "../../lib/investApi";

const CATEGORY_LABEL = {
  SUV: "SUV Token",
  SEDAN: "Sedan Token",
  HATCHBACK: "Hatchback Token",
  TRUCK: "Truck Token",
  SPORTS: "Sports Token",
};

const CATEGORY_ACCENT = {
  SUV: "#2563EB",
  SEDAN: "#E11D48",
  HATCHBACK: "#059669",
  TRUCK: "#ea580c",
  SPORTS: "#9333ea",
};

const STATUS_CONFIG = {
  PENDING:          { label: "Pending",          bg: "#FEF3C7", color: "#92400E", Icon: Clock },
  PROCESSING:       { label: "Processing",       bg: "#EEF4FF", color: "#1D4ED8", Icon: Loader2 },
  AWAITING_PAYMENT: { label: "Awaiting Payment", bg: "#FEF3C7", color: "#92400E", Icon: Clock },
  PAID:             { label: "Paid",             bg: "#EEF4FF", color: "#1D4ED8", Icon: CheckCircle2 },
  TRANSFERRING:     { label: "Transferring",     bg: "#EEF4FF", color: "#1D4ED8", Icon: ArrowRight },
  COMPLETED:        { label: "Completed",        bg: "#D1FAE5", color: "#065F46", Icon: CheckCircle2 },
  FAILED:           { label: "Failed",           bg: "#FEE2E2", color: "#991B1B", Icon: AlertCircle },
  TRANSFER_FAILED:  { label: "Transfer Failed",  bg: "#FEE2E2", color: "#991B1B", Icon: AlertCircle },
};

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

function SkeletonRow() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderBottom: "1px solid var(--zc-border)", animation: "pulse 1.5s ease-in-out infinite" }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--zc-bg-3)", flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
        <div style={{ height: 12, width: "40%", borderRadius: 4, background: "var(--zc-bg-3)" }} />
        <div style={{ height: 10, width: "25%", borderRadius: 4, background: "var(--zc-bg-3)" }} />
      </div>
      <div style={{ height: 10, width: 60, borderRadius: 4, background: "var(--zc-bg-3)" }} />
      <div style={{ height: 22, width: 80, borderRadius: 20, background: "var(--zc-bg-3)" }} />
    </div>
  );
}

export default function InvestOrdersTab({ walletUserId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!walletUserId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getOrders(walletUserId);
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [walletUserId]);

  useEffect(() => { load(); }, [load]);

  if (!walletUserId) {
    return (
      <div style={{ textAlign: "center", padding: "72px 0", color: "var(--zc-text-3)" }}>
        <ClipboardList size={36} style={{ margin: "0 auto 14px", display: "block", opacity: 0.35 }} />
        <p style={{ fontSize: 14 }}>Sign in to view order history.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 780 }}>
      <div className="zc-card" style={{ padding: 0, overflow: "hidden" }}>

        {/* Card header */}
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid var(--zc-border)",
          background: "var(--zc-bg-2)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: "var(--zc-secondary-soft)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ClipboardList size={15} style={{ color: "var(--zc-secondary)" }} />
            </div>
            <span style={{ fontWeight: 600, fontSize: 13, color: "var(--zc-text)" }}>Order History</span>
            {orders.length > 0 && (
              <span style={{
                fontSize: 11, fontWeight: 600,
                padding: "2px 8px", borderRadius: 20,
                background: "var(--zc-secondary-soft)",
                color: "var(--zc-secondary)",
              }}>
                {orders.length}
              </span>
            )}
          </div>

          <button
            onClick={load}
            disabled={loading}
            aria-label="Refresh orders"
            style={{
              width: 32, height: 32,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1.5px solid var(--zc-border)",
              borderRadius: 8,
              background: "transparent",
              cursor: loading ? "default" : "pointer",
              color: "var(--zc-text-3)",
              transition: "border-color 0.15s, color 0.15s, background 0.15s",
              opacity: loading ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.borderColor = "var(--zc-secondary)"; e.currentTarget.style.color = "var(--zc-secondary)"; e.currentTarget.style.background = "var(--zc-secondary-soft)"; }}}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--zc-border)"; e.currentTarget.style.color = "var(--zc-text-3)"; e.currentTarget.style.background = "transparent"; }}
          >
            <RefreshCw size={13} style={{ animation: loading ? "spin 0.8s linear infinite" : "none" }} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: "12px 20px", background: "#FEF2F2", borderBottom: "1px solid #FECACA", color: "var(--zc-danger)", fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
            <AlertCircle size={14} style={{ flexShrink: 0 }} />
            {error}
          </div>
        )}

        {/* Column header */}
        {!loading && orders.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 80px 100px 110px",
            padding: "8px 20px",
            borderBottom: "1px solid var(--zc-border)",
            background: "var(--zc-bg-3)",
          }}>
            {["Collection", "Qty", "Total", "Status"].map(h => (
              <span key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--zc-text-3)" }}>{h}</span>
            ))}
          </div>
        )}

        {/* Rows */}
        {loading && orders.length === 0 ? (
          <><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: "center", padding: "56px 20px" }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: "var(--zc-bg-3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 14px", fontSize: 24,
            }}>📋</div>
            <p style={{ fontWeight: 600, fontSize: 14, color: "var(--zc-text-2)", marginBottom: 4 }}>No orders yet</p>
            <p style={{ fontSize: 13, color: "var(--zc-text-3)" }}>Your purchase history will appear here.</p>
          </div>
        ) : (
          orders.map(o => {
            const sc = STATUS_CONFIG[o.status] ?? { label: o.status, bg: "var(--zc-bg-3)", color: "var(--zc-text-2)", Icon: Clock };
            const { Icon: StatusIcon } = sc;
            const accent = CATEGORY_ACCENT[o.category] ?? "var(--zc-secondary)";

            return (
              <div
                key={o.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 80px 100px 110px",
                  alignItems: "center",
                  padding: "14px 20px",
                  borderBottom: "1px solid var(--zc-border)",
                  borderLeft: `3px solid ${accent}`,
                  transition: "background 0.15s",
                  cursor: "default",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--zc-bg-3)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                {/* Collection */}
                <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingRight: 12 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: "var(--zc-text)" }}>
                    {CATEGORY_LABEL[o.category] ?? o.collectionName ?? o.collectionId}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--zc-text-3)", fontVariantNumeric: "tabular-nums" }}>
                    {fmtDate(o.createdAt)}
                  </span>
                </div>

                {/* Qty */}
                <span style={{ fontWeight: 600, fontSize: 14, fontVariantNumeric: "tabular-nums", color: "var(--zc-text)" }}>{o.quantity}</span>

                {/* Total */}
                <span style={{ fontWeight: 500, fontSize: 13, fontVariantNumeric: "tabular-nums", color: "var(--zc-text-2)" }}>
                  {o.fees ? formatCents(o.fees.totalCents) : "—"}
                </span>

                {/* Status badge */}
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "4px 10px",
                  borderRadius: 20,
                  background: sc.bg,
                  color: sc.color,
                  fontSize: 11, fontWeight: 700,
                  width: "fit-content",
                }}>
                  <StatusIcon size={10} style={{ flexShrink: 0 }} />
                  {sc.label}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
